import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SearchPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState(['Free furniture', 'Help needed', 'Services']);

  const handleSearch = async (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const userLocation = JSON.parse(localStorage.getItem('userLocation') || '{"lat": 12.9716, "lng": 77.5946}');
      
      const response = await axios.get(`${API}/posts`, {
        params: {
          latitude: userLocation.lat,
          longitude: userLocation.lng,
          radius: 10000
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Client-side filtering
      const filtered = response.data.filter(post =>
        post.title.toLowerCase().includes(query.toLowerCase()) ||
        post.description.toLowerCase().includes(query.toLowerCase()) ||
        post.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
      );
      
      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery) {
        handleSearch(searchQuery);
      }
    }, 500);

    return () => clearTimeout(debounce);
  }, [searchQuery]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Search */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="flex items-center gap-3 px-4 py-3">
          <button onClick={() => navigate('/home')}>
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search posts, tags..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 rounded-full"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2"
              >
                <X className="w-5 h-5 text-gray-400" />
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="p-4">
        {/* Recent Searches */}
        {!searchQuery && (
          <div className="mb-6">
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">Recent Searches</h2>
            <div className="space-y-2">
              {recentSearches.map((search, idx) => (
                <button
                  key={idx}
                  onClick={() => setSearchQuery(search)}
                  className="flex items-center gap-3 w-full p-3 bg-white rounded-xl border border-gray-200 hover:bg-gray-50 transition"
                >
                  <Search className="w-5 h-5 text-gray-400" />
                  <span className="text-gray-700">{search}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search Results */}
        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-600 mx-auto"></div>
          </div>
        ) : searchQuery && searchResults.length === 0 ? (
          <div className="text-center py-20">
            <Search className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No results found for "{searchQuery}"</p>
          </div>
        ) : searchResults.length > 0 ? (
          <div>
            <h2 className="text-sm font-semibold text-gray-500 uppercase mb-3">
              {searchResults.length} Results
            </h2>
            <div className="space-y-3">
              {searchResults.map((post) => (
                <div
                  key={post.id}
                  onClick={() => navigate(`/post/${post.id}`)}
                  className="bg-white p-4 rounded-xl border border-gray-200 cursor-pointer hover:shadow-md transition"
                >
                  <h4 className="font-bold text-gray-900 mb-1">{post.title}</h4>
                  <p className="text-sm text-gray-600 line-clamp-2 mb-2">{post.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>{post.user_name}</span>
                    <span className="px-2 py-1 bg-gray-100 rounded-full">{post.category}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
}